import React, { useMemo, useState } from 'react';

import {
  PageableTableProps,
  PresentationalTableProps,
  SortableTableProps,
  createTableInformationContext,
} from './PresentationalTable';
import { Order } from './types';

function isUndefinedOrNull(a: unknown) {
  return a === undefined || a === null;
}

function descendingComparator<T>(
  a: T,
  b: T,
  orderBy: keyof T,
  direction: number,
) {
  const aValue = a[orderBy];
  const bValue = b[orderBy];

  if (isUndefinedOrNull(aValue) && isUndefinedOrNull(bValue)) {
    return 0;
  }
  if (isUndefinedOrNull(aValue)) {
    return 1;
  }
  if (isUndefinedOrNull(bValue)) {
    return -1;
  }
  // undefined 는 무시

  if (bValue < aValue) {
    return -1 * direction;
  }
  if (bValue > aValue) {
    return 1 * direction;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy, 1)
    : (a, b) => descendingComparator(a, b, orderBy, -1);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number,
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

type ClientSideControlledTableProps<D> = Exclude<
  keyof PageableTableProps<D> | keyof SortableTableProps<D>,
  'countPerPageOptions' | 'page' | 'onChangePage'
>;

interface ClientSideSortableTableProps<D, K extends keyof D>
  extends Omit<
    PresentationalTableProps<D, K>,
    ClientSideControlledTableProps<D>
  > {
  initialOrderBy?: keyof D;
  initialOrder?: Order;
  /** @deprecated 특정 값에 해당하는 데이터를 맨끝으로 이동 */
  onOrderDataByStatus?(T: D[]): D[];
}

export const createClientsideSortableTableContext = <
  D,
  K extends keyof D,
>() => {
  const { TableContext, TableInformationProvider, ...rest } =
    createTableInformationContext<D, K>();
  const ClientSideSortableTableProvider = (
    props: ClientSideSortableTableProps<D, K> & React.PropsWithChildren,
  ) => {
    const { children, ...other } = props;
    const {
      data,
      identifyKey,
      page,
      onChangePage,
      countPerPageOptions = [5],
      initialOrder = 'desc',
      initialOrderBy = identifyKey,
      onOrderDataByStatus,
      onSelectAll,
    } = other;
    const [order, setOrder] = useState(initialOrder);
    const [orderBy, setOrderBy] = useState(initialOrderBy);
    const [countPerPage, setCountPerPage] = useState(countPerPageOptions[0]);

    /** manual하게 sorting해도 맨끝으로 이동한 데이터가 변하지 않음 */
    const dataToShow = useMemo(() => {
      const stableSortData = stableSort(data, getComparator(order, orderBy));
      const lastOrderedData = onOrderDataByStatus
        ? onOrderDataByStatus(stableSortData)
        : stableSortData;
      return lastOrderedData.slice(
        (page ?? 0) * countPerPage,
        (page ?? 0) * countPerPage + countPerPage,
      );
    }, [data, order, orderBy, onOrderDataByStatus, page, countPerPage]);

    return (
      <TableInformationProvider
        {...other}
        dataToShow={dataToShow}
        data={dataToShow}
        totalCount={data.length}
        page={page}
        onChangePage={onChangePage}
        countPerPage={countPerPage}
        onChangeCountPerPage={setCountPerPage}
        order={order}
        orderBy={orderBy}
        onChangeOrder={newOrder => setOrder(newOrder)}
        onChangeOrderBy={(newOrderBy, newOrder) => {
          setOrderBy(newOrderBy);
          if (newOrder) {
            setOrder(newOrder);
          }
        }}
        onSelectAll={(checked, _) => onSelectAll?.(checked, dataToShow)}>
        {children}
      </TableInformationProvider>
    );
  };

  return {
    TableContext,
    TableInformationProvider: ClientSideSortableTableProvider,
    ...rest,
  };
};
